import { AxiosResponse } from "axios";

import { pageSize } from "../../Components/Common/styles/table";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import {
  setParticipantsLoader,
  setParticipantsCount,
  setParticipantsDetails,
  setParticipantsPage,
  setColumnData,
  setUnfilteredTotal,
} from "../reducers/participantsSlice";
import { AppThunk } from "../store";
import { DateTime } from "luxon";
import { permissionAccess, permissions } from "../../utils/roles";

export const fetchParticipantsList =
  (
    page: number,
    sortOrder: string,
    sortColumn: string,
    siteIds: string[],
    status: string[],
    isLocked: string,
    screenerCompleted: string,
    screenerReceived: string,
    screenerCompletedFrom: string,
    screenerCompletedTo: string,
    cohortId: string[],
    tagIds: string[],
    customHeaderValues: { key: string; value: string | string[] }[],
    notesQuery: string,
    searchField: string,
    searchQuery: string,
    id: any
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setParticipantsLoader(true));
      const dataEditable = getState().study.dataEditable;
      let url: string = `/participants?studyId=${id}&page=${
        page + 1
      }&size=${pageSize}`;

      if (sortOrder && sortColumn) {
        url += `&sortOrder=${sortOrder}&sortKey=${sortColumn}`;
      }

      if (siteIds && siteIds.length > 0) {
        const siteIdsParam = siteIds.join(",");
        url += `&sitesIds=${siteIdsParam}`;
      }
      if (status && status.length > 0) {
        url += `&eligibilityStatus=${status.join(",")}`;
      }
      if (isLocked) {
        url += `&isLocked=${isLocked === "yes"}`;
      }
      if (screenerCompleted) {
        url += `&hasCompletedScreener=${screenerCompleted === "yes"}`;
      }
      if (screenerReceived) {
        url += `&hasReceivedScreener=${screenerReceived === "yes"}`;
      }
      if (screenerCompletedFrom) {
        url += `&screenerCompletedFrom=${screenerCompletedFrom}`;
      }
      if (screenerCompletedTo) {
        url += `&screenerCompletedTo=${screenerCompletedTo}`;
      }
      if (cohortId && cohortId.length > 0) {
        url += `&participantGroupId=${cohortId.join(",")}`;
      }
      if (tagIds && tagIds.length > 0) {
        url += `&tagIds=${tagIds.join(",")}`;
      }
      if (customHeaderValues && customHeaderValues.length > 0) {
        const customFields: Record<string, string | string[]> = {};

        customHeaderValues.forEach((field) => {
          customFields[field.key] = field.value;
        });
        const customColumns = JSON.stringify(customFields);

        url += `&customColumns=${customColumns}`;
      }
      if (notesQuery) {
        url += `&notesQuery=${encodeURIComponent(notesQuery)}`;
      }
      if (searchField && searchQuery) {
        url += `&searchField=${encodeURIComponent(searchField)}&searchQuery=${encodeURIComponent(searchQuery)}`;
      }

      const res: AxiosResponse = await http.get(url);
      const data = res?.data?.data?.data;
      const columns = res?.data?.data?.customColumns;

      const newData = data?.map((item: any) => {
        const extraHeaders: any = {};
        columns.forEach((column: any) => {
          const value = item?.customColumns.find(
            (customCol: any) => customCol.columnName === column.questionId
          )?.value;

          if (value !== undefined) {
            extraHeaders[column.varname] = {
              value,
              questionLabel: column.questionLabel,
            };
          }
        });

        const isExcluded = item.eligibilityStatus === "excluded";
        const isWithdrawn = item.eligibilityStatus === "withdrawn";
        return {
          id: item?.id,
          participantId: item?.subjectId,
          displayId:
            item?.subjectId && item?.groupCode
              ? `${item?.subjectId} - ${item?.groupCode}`
              : item?.subjectId || item?.groupCode || "",
          groupCode: item?.groupCode || "",
          email: item?.email,
          firstName: item?.firstName || "",
          lastName: item?.lastName || "",
          site: item?.site?.name,
          tags: item?.tags?.map((tag: any) => tag?.tag?.name) || [],
          randomization: item?.randomizationGroup?.treatmentGroupName || "-",
          cohort: item?.participantGroupMember?.participantGroup?.name || "-",
          hasCompletedScreener: item?.hasCompletedScreener || false,
          hasReceivedScreener: item?.hasReceivedScreener || false,
          screenerCompletedAt: item?.screenerCompletedAt || null,
          progress:
            item?.visitCompletions?.map((item: any) => ({
              id: item?.phaseId,
              phaseName: item?.phaseName || "-",
              status:
                item?.totalForms > 0 &&
                item?.completedForms === item?.totalForms
                  ? "completed"
                  : item?.completedForms > 0 || item?.inprogressForms > 0
                  ? "inprogress"
                  : "not_started",
            })) || "-",
          surveyProgress:
            item?.surveyAssignments && item?.surveyAssignments?.length
              ? item?.surveyAssignments?.map((item: any) => ({
                  id: item?.id,
                  phaseName: item?.package?.name || "",
                  status: item?.status,
                  formStatus:
                    item?.totalForms > 0 &&
                    item?.completedForms === item?.totalForms
                      ? "completed"
                      : item?.completedForms > 0 || item?.inprogressForms > 0
                      ? "inprogress"
                      : "not_started",
                  sentAt: item?.sentAt
                    ? DateTime.fromISO(item?.sentAt).toFormat("LLL dd yyyy")
                    : "",
                  formProgress:
                    item?.completedForms && item?.totalForms
                      ? (item?.completedForms / item?.totalForms) * 100
                      : 0,
                }))
              : [],
          createdOn: DateTime.fromISO(item.createdAt).toFormat("MM/dd/yyyy"),
          enrolledOn: item?.enrolledOn
            ? DateTime.fromISO(item.enrolledOn).toFormat("LLL dd yyyy")
            : "-",
          status: item?.eligibilityStatus || "-",
          notesCount: parseInt(item?.notesCount || "0"),
          isLocked: item?.isLocked || false,
          isArchived: item?.isArchived || false,
          canLock:
            permissionAccess(
              item?.site?.id,
              permissions.recordLockUnlock,
              getState().study.sitePermissions
            ) &&
            !isWithdrawn &&
            dataEditable,
          canArchive:
            permissionAccess(
              item?.site?.id,
              permissions.recordArchive,
              getState().study.sitePermissions
            ) && dataEditable,
          canWithdraw:
            permissionAccess(
              item?.site?.id,
              permissions.editParticipant,
              getState().study.sitePermissions
            ) &&
            !isExcluded &&
            !isWithdrawn &&
            dataEditable,
          canAssignConsent:
            permissionAccess(
              item?.site?.id,
              permissions.editParticipant,
              getState().study.sitePermissions
            ) &&
            item.eligibilityStatus === "included" &&
            dataEditable,
          ...extraHeaders,
        };
      });

      if (data?.length === 0 && res?.data?.data?.count > 0 && page > 0) {
        dispatch(
          setParticipantsPage({
            page: 0,
            pageSize,
          })
        );
        return;
      }
      dispatch(setColumnData(columns));
      dispatch(setParticipantsDetails(newData));
      dispatch(setParticipantsCount(res?.data?.data?.count));
      dispatch(
        setUnfilteredTotal(
          res?.data?.data?.totalCount ?? res?.data?.data?.count ?? 0,
        ),
      );
      dispatch(setParticipantsLoader(false));
    } catch (err) {
      errorToastMessage(err as Error);
      dispatch(setParticipantsLoader(false));
    }
  };